import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../../services/api';
import { FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import '../Students/StudentsPage.css';

const LevelsPage = () => {
    const [levels, setLevels] = useState([]);
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingLevel, setEditingLevel] = useState(null);
    const [selectedArea, setSelectedArea] = useState('');

    const [formData, setFormData] = useState({
        areaId: '',
        name: '',
        description: '',
        sortOrder: 0
    });

    useEffect(() => {
        loadData();
    }, [selectedArea]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [areasResponse, levelsResponse] = await Promise.all([
                adminAPI.getAreas(),
                adminAPI.getLevels(selectedArea || undefined)
            ]);
            setAreas(areasResponse.data.areas);
            setLevels(levelsResponse.data.levels);
            setError('');
        } catch (err) {
            setError('Ошибка при загрузке данных');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingLevel) {
                await adminAPI.updateLevel(editingLevel.id, formData);
            } else {
                await adminAPI.createLevel(formData);
            }
            
            setShowModal(false);
            setEditingLevel(null);
            resetForm();
            loadData();
        } catch (err) {
            setError(err.response?.data?.error || 'Ошибка при сохранении уровня');
        }
    };

    const handleEdit = (level) => {
        setEditingLevel(level);
        setFormData({
            areaId: level.area_id,
            name: level.name,
            description: level.description || '',
            sortOrder: level.sort_order || 0
        });
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Вы уверены, что хотите удалить этот уровень? Все связанные уроки также будут удалены!')) {
            return;
        }

        try {
            await adminAPI.deleteLevel(id);
            loadData();
        } catch (err) {
            setError('Ошибка при удалении уровня');
        }
    };

    const resetForm = () => {
        setFormData({
            areaId: '',
            name: '',
            description: '',
            sortOrder: 0
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingLevel(null);
        resetForm();
    };

    if (loading) {
        return <div className="loading">Загрузка...</div>;
    }

    return (
        <div className="container">
            <div className="page-header">
                <div>
                    <h1>Уровни обучения</h1>
                    <p>Управление уровнями (A1, A2, B1, B2 и т.д.)</p>
                </div>
                <button 
                    className="btn btn-primary"
                    onClick={() => setShowModal(true)}
                >
                    <FiPlus /> Добавить уровень
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="card" style={{ marginBottom: '20px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                    <label>Фильтр по области:</label>
                    <select 
                        value={selectedArea} 
                        onChange={(e) => setSelectedArea(e.target.value)}
                        style={{ width: '300px' }}
                    >
                        <option value="">Все области</option>
                        {areas.map(area => (
                            <option key={area.id} value={area.id}>
                                {area.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="card">
                <div className="students-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Порядок</th>
                                <th>Область</th>
                                <th>Название</th>
                                <th>Описание</th>
                                <th>Статус</th>
                                <th>Дата создания</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {levels.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center' }}>
                                        Нет уровней. Создайте первый уровень.
                                    </td>
                                </tr>
                            ) : (
                                levels.map((level) => (
                                    <tr key={level.id}>
                                        <td>{level.sort_order}</td>
                                        <td>{level.area_name}</td>
                                        <td><strong>{level.name}</strong></td>
                                        <td>{level.description || '—'}</td>
                                        <td>
                                            <span className={`status-badge ${level.is_active ? 'active' : 'inactive'}`}>
                                                {level.is_active ? 'Активен' : 'Неактивен'}
                                            </span>
                                        </td>
                                        <td>
                                            {new Date(level.created_at).toLocaleDateString('ru-RU')}
                                        </td>
                                        <td>
                                            <div className="table-actions">
                                                <button
                                                    className="btn-icon"
                                                    onClick={() => handleEdit(level)}
                                                    title="Редактировать"
                                                >
                                                    <FiEdit />
                                                </button>
                                                <button
                                                    className="btn-icon btn-danger"
                                                    onClick={() => handleDelete(level.id)}
                                                    title="Удалить"
                                                >
                                                    <FiTrash2 />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{editingLevel ? 'Редактировать уровень' : 'Добавить уровень'}</h2>
                            <button onClick={handleCloseModal} className="modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            <div className="form-group">
                                <label>Область обучения *</label>
                                <select
                                    name="areaId"
                                    value={formData.areaId}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="">Выберите область</option>
                                    {areas.map(area => (
                                        <option key={area.id} value={area.id}>
                                            {area.name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Название уровня *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    placeholder="Например: A1, A2, B1"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Описание</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    placeholder="Краткое описание уровня"
                                    rows="3"
                                />
                            </div>

                            <div className="form-group">
                                <label>Порядок сортировки</label>
                                <input
                                    type="number"
                                    name="sortOrder"
                                    value={formData.sortOrder}
                                    onChange={handleInputChange}
                                    min="0"
                                />
                            </div>

                            <div className="modal-footer">
                                <button type="button" className="btn" onClick={handleCloseModal}>
                                    Отмена
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {editingLevel ? 'Сохранить' : 'Создать'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style>{`
                .status-badge {
                    padding: 4px 12px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }
                .status-badge.active {
                    background-color: #f6ffed;
                    color: #52c41a;
                    border: 1px solid #b7eb8f;
                }
                .status-badge.inactive {
                    background-color: #fff2f0;
                    color: #f5222d;
                    border: 1px solid #ffccc7;
                }
            `}</style>
        </div>
    );
};

export default LevelsPage;
